import { useGetEntryById, useGetPortalGeneralPageById } from '../../api/contentful/contentfulQueries'
import { PreviewGuard } from '../../components/PreviewGuard'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { BodyLarge, Title1 } from '../../components/styles/Typography'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import {
  useSelectFirstName,
  useSelectScreeningStatus,
  useSelectUserGender,
  useSelectUserIsManager,
  useSelectUserMarket,
} from '../../state/selectors'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { TextWithImageCard } from '../../components/TextWithImageCard'
import { isITextWithImageBlock } from '@estercare/ester-shared'
import { Gender } from '../../api/ester-b2b-api/private/users/types'
import { useTranslation } from 'react-i18next'
import { CONTENTFUL_ENTRY_IDS } from '../../constants/contentfulEntries'
import { DigitalHealthConsultationBanner } from '../../components/DigitalHealthConsultationBanner'
import { useGetHealthCallAppointmentQuery } from '../../api/ester-b2b-api/private/healthCalls/healthCallQueries'
import { isHealthCallsEnabled, isSwedishMarket } from '../../utils/market'
import { HealthPlanCircles } from '../../components/HealthPlanCircles'
import { ArticlesAndPodcastsSection } from '../../components/ArticlesAndPodcastsSection'
import { PodcastHighlightSection } from '../../components/PodcastHighlightSection'
import { MessageSection } from '../../components/MessageSection'
import { MessageNotificationBanner } from '../../components/MessageNotificationBanner'
import { useUnreadCaseMessagesCount } from '../../api/ester-b2b-api/private/case/caseQueries'
import { getHomeBannerEntryId } from '../../utils/homeBanner'

export const Home = () => {
  const { t } = useTranslation()
  const firstName = useSelectFirstName()
  const gender = useSelectUserGender()
  const isManager = useSelectUserIsManager()
  const userMarket = useSelectUserMarket()
  const homePage = useGetPortalGeneralPageById(CONTENTFUL_ENTRY_IDS.HOME_EMPLOYEE)
  const healthCallAppointment = useGetHealthCallAppointmentQuery(isHealthCallsEnabled(userMarket))
  const { data: unreadCaseMessagesCount } = useUnreadCaseMessagesCount()

  const screeningStatus = useSelectScreeningStatus()
  const isMaleUser = gender === Gender.M
  const homeBannerEntryId = getHomeBannerEntryId({ screeningStatus, isMaleUser, isManager })
  const bannerBlock = useGetEntryById(homeBannerEntryId!).data

  const homeContent = homePage?.data?.fields
  const { description } = homeContent || {}

  if (homePage.isLoading) return <LoadingSpinner />
  if (!homeContent) return <PreviewGuard />

  const appointment = healthCallAppointment.data

  return (
    <>
      <ComponentLayout paddingBottom={0}>
        <HeadingContainer>
          <StyledTitle1>
            {firstName ? `${t('employee.home.hello')} ${firstName}!` : `${t('employee.home.hello')}!`}
          </StyledTitle1>
          <BodyLarge>{description}</BodyLarge>
          {!!unreadCaseMessagesCount && <MessageNotificationBanner count={unreadCaseMessagesCount} />}
        </HeadingContainer>
      </ComponentLayout>

      {isHealthCallsEnabled(userMarket) && appointment && (
        <ComponentLayout paddingBottom={0} paddingTop={theme.spacing.mediumLarge}>
          <DigitalHealthConsultationBanner
            appointment={appointment}
            linkUrl="/portal/health-services/health-consultation"
          />
        </ComponentLayout>
      )}
      <ComponentLayout paddingBottom={0} paddingTop={theme.spacing.mediumLarge}>
        {isITextWithImageBlock(bannerBlock) && <TextWithImageCard {...bannerBlock.fields} />}
      </ComponentLayout>
      {!isMaleUser && <HealthPlanCircles />}
      <ArticlesAndPodcastsSection />
      {isSwedishMarket(userMarket) && <PodcastHighlightSection />}
      <MessageSection />
    </>
  )
}

const HeadingContainer = styled.div`
  max-width: 640px;
`

const StyledTitle1 = styled(Title1)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
